/**
 * ID Helper Utilities
 *
 * Type-safe utilities for handling IDs throughout the application
 * Provides consistent ID handling across the application
 */

import type { PickemQuestion } from '@bet99-pickem/api-types';

// =============================================================================
// Generic ID Conversion
// =============================================================================

/**
 * Convert any ID value to string
 * Handles string, number, null, and undefined
 *
 * @param id - The ID value to convert
 * @returns String representation of the ID, or empty string if null/undefined
 */
export function toId(id: string | number | undefined | null): string {
  if (id === undefined || id === null || id === '') return '';
  return String(id);
}

/**
 * Convert user ID to string
 *
 * @param userId - The user ID to convert
 * @returns String representation of the user ID
 */
export function toUserId(userId: string | number | undefined | null): string {
  return toId(userId);
}

/**
 * Convert league ID to string
 *
 * @param leagueId - The league ID to convert
 * @returns String representation of the league ID
 */
export function toLeagueId(leagueId: string | number | undefined | null): string {
  return toId(leagueId);
}

// =============================================================================
// Question and Option ID Helpers
// =============================================================================

/**
 * Get question ID as string
 * Handles the API returning either string or number
 */
export function getQuestionId(question: PickemQuestion): string {
  // question_id is already a string in the API, but handle both cases for safety
  return String(question.question_id);
}

/**
 * Get option ID as string
 * Handles the API returning either string or number
 */
export function getOptionId(option: PickemQuestion['options'][0]): string {
  // option_id is already a string in the API, but handle both cases for safety
  return String(option.option_id);
}

/**
 * Find option by ID in a question
 */
export function findOptionById(
  question: PickemQuestion,
  optionId: string
): PickemQuestion['options'][0] | undefined {
  return question.options.find(opt => String(opt.option_id) === optionId);
}

/**
 * Find option by title match (case-insensitive)
 */
export function findOptionByTitle(
  question: PickemQuestion,
  searchTerm: string
): PickemQuestion['options'][0] | undefined {
  const lowerSearch = searchTerm.toLowerCase();
  return question.options.find(opt => opt.title_en?.toLowerCase().includes(lowerSearch));
}

/**
 * Get option IDs as array of strings
 */
export function getOptionIds(question: PickemQuestion): string[] {
  return question.options.map(opt => String(opt.option_id));
}

/**
 * Check if a selection matches a question
 */
export function selectionMatchesQuestion(
  questionId: string | number,
  selectionQuestionId: string | number
): boolean {
  return String(questionId) === String(selectionQuestionId);
}
